import { ConfirmDialog } from "@/components/dialog/confirmation";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  deleteEnrollment,
  markComplete,
  updateEnrollmentStatus,
} from "@/store/features/enroll-slice";
import { dispatch, useSelector } from "@/store/store";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataTableColumnHeader } from "./data-table-column-header";

export type Enrollment = {
  id: string;
  createdAt: string;
  enrolledAt: string;
  completedAt: string;
  fullName: string;
  email: string;
  mobile: string;
  is_enrolled: boolean;
  is_completed: boolean;
  message: string;
  address: string;
  academicLevel: string;
  course: {
    id: string;
    name: string;
    duration: string;
    status: "active" | "inactive";
    price: number;
  };
};

const ActionsCell = ({ enrollment }: { enrollment: Enrollment }) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false); // Control dialog state

  const loading = useSelector((state) => state.enrollment.isLoading);

  const handleDelete = (id: string) => {
    dispatch(
      deleteEnrollment({
        id,
        onClose: () => setOpen(false),
      })
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => navigate(`/enrollments/${enrollment.id}`)}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          Delete
        </DropdownMenuItem>

        <Dialog onOpenChange={setOpen} open={open}>
          <DialogTrigger asChild></DialogTrigger>
          <ConfirmDialog
            loading={loading}
            title={`Delete ${enrollment?.fullName}'s enrollment?`}
            onOk={() => handleDelete(enrollment?.id)}
            onClose={() => setOpen(false)}
          />
        </Dialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SwitchAction = ({ enrollment }: { enrollment: Enrollment }) => {
  const [open, setOpen] = useState<boolean>(false); // Control dialog state

  const loading = useSelector((state) => state.enrollment.isLoading);
  const updateEnroll = (value: boolean, enrollId: string) => {
    dispatch(
      updateEnrollmentStatus({
        data: {
          is_enrolled: value,
        },
        id: enrollId,
        onClose: () => setOpen(false),
      })
    );
  };

  return (
    <>
      <Switch
        checked={enrollment?.is_enrolled}
        onCheckedChange={() => setOpen(true)}
      />

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogTrigger asChild></DialogTrigger>
        <ConfirmDialog
          loading={loading}
          title={`${enrollment?.is_enrolled ? "Un-Enroll" : "Enroll"} ${
            enrollment?.fullName
          }'s enrollment?`}
          onOk={() => updateEnroll(!enrollment?.is_enrolled, enrollment?.id)}
          onClose={() => setOpen(false)}
        />
      </Dialog>
    </>
  );
};

const SwitchActionComplete = ({ enrollment }: { enrollment: Enrollment }) => {
  const [open, setOpen] = useState<boolean>(false); // Control dialog state

  const loading = useSelector((state) => state.enrollment.isLoading);
  const completeEnroll = (value: boolean, enrollId: string) => {
    dispatch(
      markComplete({
        data: {
          is_completed: value,
        },
        id: enrollId,
        onClose: () => setOpen(false),
      })
    );
  };

  return (
    <>
      <Switch
        checked={enrollment?.is_completed}
        onCheckedChange={() => setOpen(true)}
      />

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogTrigger asChild></DialogTrigger>
        <ConfirmDialog
          loading={loading}
          title={`${enrollment?.is_completed ? "Un-Complete" : "Complete"} ${
            enrollment?.fullName
          }'s enrollment?`}
          onOk={() => completeEnroll(!enrollment?.is_completed, enrollment?.id)}
          onClose={() => setOpen(false)}
        />
      </Dialog>
    </>
  );
};

export const columns: ColumnDef<Enrollment>[] = [
  {
    accessorKey: "fullName",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => <div>{row.getValue("fullName")}</div>,
  },

  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => <div>{row.getValue("email")}</div>,
  },

  {
    accessorKey: "mobile",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Phone
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => <div>{row.getValue("mobile")}</div>,
  },

  {
    accessorKey: "address",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Address
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => {
      return <div>{row.getValue("address")}</div>;
    },
  },

  {
    id: "course",
    accessorKey: "course",
    header: ({ column }) => {
      return (
        <div className="flex items-center space-x-2">
          <span className="flex items-center font-bold">Course</span>
        </div>
      );
    },
    cell: ({ row }) => {
      return (
        <Link
          to={`/courses/${row?.original?.course?.id}`}
          className="underline"
        >
          {row.original?.course?.name}
        </Link>
      );
    },
  },

  {
    accessorKey: "is_enrolled",
    header: ({ column }) => {
      return (
        <div className="flex items-center space-x-2">
          <span className="flex items-center font-bold">Enrolled?</span>
          {/* <FilterSelect column={column} options={[1, 0]} /> */}
        </div>
      );
    },
    cell: ({ row }) => {
      return <SwitchAction enrollment={row?.original} />;
    },
  },

  {
    accessorKey: "is_completed",
    header: ({ column }) => {
      return (
        <div className="flex items-center space-x-2">
          <span className="flex items-center font-bold">Completed?</span>
          {/* <FilterSelect column={column} options={[1, 0]} /> */}
        </div>
      );
    },
    cell: ({ row }) => {
      return <SwitchActionComplete enrollment={row?.original} />;
    },
  },

  {
    accessorKey: "enrolledAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Enrolled At" />
    ),
    cell: ({ row }) => (
      <div>{moment(row.getValue("enrolledAt")).format("YYYY-MM-DD")}</div>
    ),
  },

  {
    accessorKey: "completedAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Completed At" />
    ),
    cell: ({ row }) => (
      <div>
        {row?.original?.is_completed
          ? moment(row.getValue("completedAt")).format("YYYY-MM-DD")
          : "-"}
      </div>
    ),
  },

  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created At" />
    ),
    cell: ({ row }) => (
      <div>{moment(row.getValue("createdAt")).format("YYYY-MM-DD")}</div>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => <ActionsCell enrollment={row.original} />,
  },
];
